/*
 * STRATEGIC PRIORITY SCORING
 * ==========================
 * Score = (quadrantPosition x 3) + categoryModifier + urgentBoost
 * Lower score = higher priority. Negative scores are valid (very high priority).
 *
 * The x3 multiplier creates "space" between quadrant positions so that
 * category modifiers can meaningfully reorder tasks without completely
 * overriding the impact/effort matrix.
 */

const QUADRANT_ORDERS = {
  high: [
    "hi-impact-lo-effort",   // 0 - Quick Wins
    "hi-impact-med-effort",  // 1 - High-Value
    "hi-impact-hi-effort",   // 2 - Big Projects (deep work at peak energy)
    "med-impact-lo-effort",  // 3 - Easy Gains
    "med-impact-med-effort", // 4 - Steady Work
    "med-impact-hi-effort",  // 5 - Consider
    "lo-impact-lo-effort",   // 6 - Fill-Ins
    "lo-impact-med-effort",  // 7 - Low Priority
    "lo-impact-hi-effort",   // 8 - Avoid
  ],
  medium: [
    "hi-impact-lo-effort",   // 0 - Quick Wins
    "med-impact-lo-effort",  // 1 - Easy Gains (low effort preferred)
    "hi-impact-med-effort",  // 2 - High-Value
    "med-impact-med-effort", // 3 - Steady Work
    "lo-impact-lo-effort",   // 4 - Fill-Ins (easy momentum)
    "lo-impact-med-effort",  // 5 - Low Priority
    "hi-impact-hi-effort",   // 6 - Big Projects (too draining)
    "med-impact-hi-effort",  // 7 - Consider
    "lo-impact-hi-effort",   // 8 - Avoid
  ],
  low: [
    "hi-impact-lo-effort",   // 0 - Quick Wins
    "med-impact-lo-effort",  // 1 - Easy Gains
    "lo-impact-lo-effort",   // 2 - Fill-Ins (all low-effort first)
    "hi-impact-med-effort",  // 3 - High-Value
    "med-impact-med-effort", // 4 - Steady Work
    "lo-impact-med-effort",  // 5 - Low Priority
    "hi-impact-hi-effort",   // 6 - Big Projects (do NOT attempt)
    "med-impact-hi-effort",  // 7 - Consider
    "lo-impact-hi-effort",   // 8 - Avoid
  ],
};

const CATEGORY_MODIFIERS = {
  high:   { daily: -2, weekly: 0, monthly: 1 },
  medium: { daily: -5, weekly: -1, monthly: 2 },
  low:    { daily: -8, weekly: -1, monthly: 3 },
};

const URGENT_BOOST = -9;

export function getPrioritizedTasks(matrixTasks, energyLevel) {
  const order = QUADRANT_ORDERS[energyLevel] || QUADRANT_ORDERS.high;
  const catMods = CATEGORY_MODIFIERS[energyLevel] || CATEGORY_MODIFIERS.high;

  return [...matrixTasks].sort((a, b) => {
    const aBase = order.indexOf(a.quadrant) * 3;
    const bBase = order.indexOf(b.quadrant) * 3;
    const aCat = catMods[a.category] || 0;
    const bCat = catMods[b.category] || 0;
    const aUrgent = a.urgent ? URGENT_BOOST : 0;
    const bUrgent = b.urgent ? URGENT_BOOST : 0;
    const aScore = aBase + aCat + aUrgent;
    const bScore = bBase + bCat + bUrgent;

    if (aScore !== bScore) return aScore - bScore;
    // Tiebreakers: urgent first, then daily > weekly > monthly
    if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;
    const catTie = { daily: 0, weekly: 1, monthly: 2 };
    return (catTie[a.category] || 0) - (catTie[b.category] || 0);
  });
}
